// ./src/components/features/Connectors/ConfigEditors/HttpConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Stack,
  TextInput,
  Select,
  Group,
  Text,
  ActionIcon,
  Paper,
  Box,
} from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { AppButton } from "@/components/generic";
import { stringifyObject, parseObject } from "@/utils";
import { HttpConnectorConfig } from "@/types";

export interface ConnectorHttpConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: HttpConnectorConfig = {
  url: "",
  verb: "POST",
  headers: [],
};

// Available HTTP verbs
const HTTP_VERBS = [
  { value: "GET", label: "GET" },
  { value: "POST", label: "POST" },
  { value: "PUT", label: "PUT" },
  { value: "PATCH", label: "PATCH" },
  { value: "DELETE", label: "DELETE" },
];

/**
 * Parse and validate HTTP connector configuration
 * Uses generic parseObject for base parsing, then validates headers array items
 */
const parseHttpConfig = (jsonString: string): HttpConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);

  // Additional validation for headers array items
  if (Array.isArray(parsed.headers)) {
    parsed.headers = parsed.headers
      .filter(
        (h: any) =>
          h &&
          typeof h === "object" &&
          typeof h.name === "string" &&
          typeof h.value === "string"
      )
      .map((h: any) => ({ name: h.name, value: h.value }));
  }

  return parsed;
};

export const ConnectorHttpConfigEditor: React.FC<
  ConnectorHttpConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<HttpConnectorConfig>(() =>
    parseHttpConfig(value)
  );

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseHttpConfig(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: HttpConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle URL change
  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, url: e.target.value });
  };

  // Handle verb change
  const handleVerbChange = (verb: string | null) => {
    if (verb) {
      updateConfig({ ...config, verb });
    }
  };

  // Handle header name change
  const handleHeaderNameChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newHeaders = [...config.headers];
    newHeaders[index] = { ...newHeaders[index], name: e.target.value };
    updateConfig({ ...config, headers: newHeaders });
  };

  // Handle header value change
  const handleHeaderValueChange = (
    index: number,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newHeaders = [...config.headers];
    newHeaders[index] = { ...newHeaders[index], value: e.target.value };
    updateConfig({ ...config, headers: newHeaders });
  };

  // Add new header
  const handleAddHeader = () => {
    updateConfig({
      ...config,
      headers: [...config.headers, { name: "", value: "" }],
    });
  };

  // Remove header
  const handleRemoveHeader = (index: number) => {
    const newHeaders = config.headers.filter((_, i) => i !== index);
    updateConfig({ ...config, headers: newHeaders });
  };

  return (
    <Stack gap="md">
      {/* URL Field */}
      <TextInput
        label="URL"
        placeholder="https://api.example.com/webhook"
        description="The endpoint URL to send requests to"
        value={config.url}
        onChange={handleUrlChange}
        disabled={disabled}
        required
        size="md"
      />

      {/* HTTP Verb Selector */}
      <Select
        label="HTTP Method"
        placeholder="Select method"
        description="The HTTP method to use for the request"
        data={HTTP_VERBS}
        value={config.verb}
        onChange={handleVerbChange}
        disabled={disabled}
        required
        size="md"
      />

      {/* Headers Section */}
      <Box>
        <Group justify="space-between" mb="xs">
          <Text fw={500} size="sm">
            Headers
          </Text>
          <AppButton
            variant="tertiary"
            size="xs"
            iconName="Plus"
            onClick={handleAddHeader}
            disabled={disabled}
          >
            Add Header
          </AppButton>
        </Group>

        {config.headers.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="md">
            No headers configured. Click "Add Header" to add one.
          </Text>
        ) : (
          <Stack gap="xs">
            {config.headers.map((header, index) => (
              <Paper key={index} p="xs" withBorder>
                <Group gap="xs" align="flex-end">
                  <TextInput
                    label={index === 0 ? "Name" : undefined}
                    placeholder="e.g., Authorization"
                    value={header.name}
                    onChange={(e) => handleHeaderNameChange(index, e)}
                    disabled={disabled}
                    size="sm"
                    style={{ flex: 1 }}
                  />
                  <TextInput
                    label={index === 0 ? "Value" : undefined}
                    placeholder="Header value"
                    value={header.value}
                    onChange={(e) => handleHeaderValueChange(index, e)}
                    disabled={disabled}
                    size="sm"
                    style={{ flex: 2 }}
                  />
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    onClick={() => handleRemoveHeader(index)}
                    disabled={disabled}
                    title="Remove header"
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Paper>
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  );
};
